import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {
    urlProjectFileDownload,
    urlProjectZipDownload,
    urlParamReplicaId,
} from '../../../Constants';
import { downloadDocumentsStream } from '../../../util/FileUtils';
import { IDocDefaultFields } from '../../project/documents/sliceProjectDocuments';
import { IAction, IAppReduxStore, SliceProps } from '../../store';
import {
    getProjectDocumentSummary,
    ISelectedDocuments,
} from '../documents/sliceProjectSelectedDocuments';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * remove after backend implementation
 */
const FIELD_NAME = 'file';
const FIELD_INDEX = '0';

/**
 * Helpers for thunks
 */
const getDownloadFileItem = (docs: IDocDefaultFields[]): string[][] => {
    return docs.map(item => [item.IdDocIndex, FIELD_NAME, item.Files.FileName, FIELD_INDEX]);
};

const getDownloadZipFileItem = (docs: string[][]): string => {
    return docs.map(doc => doc.join('|')).join(';');
};

const _getDocumentsSummary = async (config: ISelectedDocuments, thunkApi): Promise<string[][]> => {
    const { replicaId, areaName, documentIds } = config;

    const viewsData = (thunkApi.getState() as IAppReduxStore).projectViews.data;

    const { viewStructure, viewId } = Object.values(viewsData).filter(
        x => x.areaId === areaName,
    )[0];

    const selectedDocsSummary = await thunkApi.dispatch(
        getProjectDocumentSummary({
            replicaId,
            documentIds,
            viewName: viewStructure,
            columnName: viewId,
            fields: ['File'],
        }),
    );

    if (!selectedDocsSummary.payload.documents.length) {
        return Promise.reject('Failed to download zip archive');
    }

    return getDownloadFileItem(selectedDocsSummary.payload.documents);
};

/**
 * Initial slice state value.
 */
const initialState: IProjectFileDownloadState = {
    isPluralDownload: false,
    loadingSingleDocument: false,
    downloadDocuments: {
        loading: false,
        error: null,
    },
    downloadZip: {
        loading: false,
        error: null,
    },
    numberOfDownloadedDocuments: 0,
    showSingleDocumentLoading: false,
};

/**
 * Create and remove download link ..TBD..
 */
const reducers = {
    /**
     * Updates the "isPluralDownload" property of the state's object.
     * @param state - Current slice state object.
     * @param action - Action containing the boolean value.
     */
    setIsPlural(state: IProjectFileDownloadState, action: IAction<boolean>) {
        state.isPluralDownload = action.payload;
    },

    resetNumberOfDownloadedDocuments(state: IProjectFileDownloadState) {
        state.numberOfDownloadedDocuments = 0;
    },

    setShowSingleDocumentLoading(state: IProjectFileDownloadState, action: IAction<boolean>) {
        state.showSingleDocumentLoading = action.payload;
    },
    resetDownloadDocumentsError(state: IProjectFileDownloadState) {
        state.downloadDocuments.error = null;
    },
    resetDownloadZipError(state: IProjectFileDownloadState) {
        state.downloadZip.error = null;
    },
};

/**
 * Object of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches document stream and downloads document.
     * @param config - Object that contains the project database's replicaId, document Id, file name, fied name, field index.
     * @returns Promise.
     */
    downloadDocument: async (config: IDownloadDocument, thunkApi): Promise<boolean> => {
        const { replicaId, fileName, idDocIndex, fieldIndex, fieldName, isPluralDownload } = config;

        const isPlural = !!isPluralDownload;

        if (isPlural) {
            thunkApi.dispatch(setIsPlural(true));
        }

        try {
            const request: RequestInit = {
                headers: {
                    idDocIndex,
                    fieldName,
                    fieldIndex,
                    fileName: encodeURIComponent(fileName),
                },
            };

            const url = urlProjectFileDownload.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            if (response.ok) {
                downloadDocumentsStream(await response.blob(), decodeURIComponent(fileName));

                return Promise.resolve(isPlural);
            }

            return Promise.reject(fileName);
        } catch (e) {
            return Promise.reject(fileName);
        }
    },

    /**
     * Async function that fetches documents data and calls thunk downloadDocument method for each document.
     * @param config - Object that contains the project database's replicaId, document Ids.
     * @param thunkApi - Provide access to the store.
     * @returns Promise.
     */
    downloadDocuments: async (config: ISelectedDocuments, thunkApi): Promise<number> => {
        const { replicaId, documentIds } = config;

        thunkApi.dispatch(setIsPlural(documentIds.length > 1));

        try {
            const fileList = await _getDocumentsSummary(config, thunkApi);

            await Promise.all(
                fileList.map(async ([idDocIndex, fieldName, fileName, fieldIndex]) =>
                    thunkApi.dispatch(
                        downloadDocument({
                            replicaId,
                            idDocIndex,
                            fieldName,
                            fileName,
                            fieldIndex,
                            isPluralDownload: true,
                        }),
                    ),
                ),
            );

            return Promise.resolve(config.documentIds.length);
        } catch (error) {
            return Promise.reject(error);
        }
    },

    /**
     * Async function that fetches document stream and downloads zip
     * @param config - Object that contains the project database's replicaId, document Ids.
     * @param thunkApi - Provide access to the store.
     * @returns Promise.
     */
    downloadZip: async (config: ISelectedDocuments, thunkApi): Promise<number> => {
        const getFileName = () => `EPLASS Export (${dayjs().format('YYYYMMDDHHmmss')})`;

        try {
            thunkApi.dispatch(setIsPlural(true));

            const { replicaId } = config;

            const fileList = getDownloadZipFileItem(await _getDocumentsSummary(config, thunkApi));

            const formData = new FormData();
            formData.append('fileList', fileList);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectZipDownload.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            if (response.ok) {
                downloadDocumentsStream(await response.blob(), getFileName());

                return Promise.resolve(config.documentIds.length);
            }

            return Promise.reject(`${getFileName()}.zip`);
        } catch (e) {
            return Promise.reject(`${getFileName()}.zip`);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectFileDownloadState>) => {
        /**
         * Reducers for the 'downloadDocument' thunk.
         */
        builder.addCase(downloadDocument.pending, (state: Draft<IProjectFileDownloadState>) => {
            state.loadingSingleDocument = true;
            state.downloadDocuments.error = null;
        });
        builder.addCase(
            downloadDocument.fulfilled,
            (state: Draft<IProjectFileDownloadState>, action: IAction<boolean>) => {
                if (!action.payload) {
                    state.numberOfDownloadedDocuments = 1;
                }
                state.loadingSingleDocument = false;
                state.downloadDocuments.error = null;
            },
        );
        builder.addCase(
            downloadDocument.rejected,
            (state: Draft<IProjectFileDownloadState>, action: IAction<SerializedError>) => {
                state.loadingSingleDocument = false;
                state.downloadDocuments.error = action.error;
            },
        );

        /**
         * Reducers for the 'downloadDocuments' thunk.
         */
        builder.addCase(downloadDocuments.pending, (state: Draft<IProjectFileDownloadState>) => {
            state.downloadDocuments.loading = true;
            state.downloadDocuments.error = null;
        });
        builder.addCase(
            downloadDocuments.fulfilled,
            (state: Draft<IProjectFileDownloadState>, action: IAction<number>) => {
                state.numberOfDownloadedDocuments = action.payload;
                state.downloadDocuments.loading = false;
                state.downloadDocuments.error = null;
            },
        );
        builder.addCase(
            downloadDocuments.rejected,
            (state: Draft<IProjectFileDownloadState>, action: IAction<SerializedError>) => {
                state.downloadDocuments.loading = false;
                state.downloadDocuments.error = action.error;
            },
        );

        /**
         * Reducers for the 'getZipUrl' thunk.
         */
        builder.addCase(downloadZip.pending, (state: Draft<IProjectFileDownloadState>) => {
            state.downloadZip.loading = true;
            state.downloadZip.error = null;
        });
        builder.addCase(
            downloadZip.fulfilled,
            (state: Draft<IProjectFileDownloadState>, action: IAction<number>) => {
                state.numberOfDownloadedDocuments = action.payload;
                state.downloadZip.loading = false;
                state.downloadZip.error = null;
            },
        );
        builder.addCase(
            downloadZip.rejected,
            (state: Draft<IProjectFileDownloadState>, action: IAction<SerializedError>) => {
                state.downloadZip.loading = false;
                state.downloadZip.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const downloadDocument = createAsyncThunk(
    `${urlProjectFileDownload}`,
    thunks.downloadDocument,
);
export const downloadDocuments = createAsyncThunk(
    `${urlProjectFileDownload}/wrapper`,
    thunks.downloadDocuments,
);
export const downloadZip = createAsyncThunk(`${urlProjectZipDownload}`, thunks.downloadZip);

/**
 * Interface that describes the download document.
 */
interface IDownloadDocument {
    replicaId: string;
    idDocIndex: string;
    fileName: string;
    fieldIndex: string;
    fieldName: string;
    isPluralDownload?: boolean;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectFileDownloadState {
    /** Allow to display downloading message correctly */
    isPluralDownload: boolean;

    /** Loading info for single document */
    loadingSingleDocument: boolean;

    /** Consist the info about loading and error regarding document(s) */
    downloadDocuments: SliceProps;

    /** Consist the info about loading and error regarding zip */
    downloadZip: SliceProps;

    numberOfDownloadedDocuments: number;
    showSingleDocumentLoading: boolean;
}

/**
 * Create the redux slice.
 */
export const projectFileDownloadSlice = createSlice({
    name: 'projectFileDownload',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const {
    setIsPlural,
    resetNumberOfDownloadedDocuments,
    setShowSingleDocumentLoading,
    resetDownloadDocumentsError,
    resetDownloadZipError,
} = projectFileDownloadSlice.actions;

/**
 * Gets loading of multiple or single document
 * @param store IAppReduxStore
 * @returns boolean
 */
export const getDocumentLoading = (store: IAppReduxStore): boolean =>
    store.projectFileDownload.downloadDocuments.loading ||
    store.projectFileDownload.loadingSingleDocument;

/**
 * Export slice reducer as default.
 */
export default projectFileDownloadSlice.reducer;
